import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, ArrowRight, Mail, Send, Users, Building2, 
  FileText, CheckCircle, AlertCircle, Loader2, Eye, Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailCampaignPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [industryFilter, setIndustryFilter] = useState("all");
  const [showPreview, setShowPreview] = useState(false);
  
  const [emailData, setEmailData] = useState({
    subject: "Join SKI AXIS - Transform Your Startup's Governance",
    template: "invitation",
    customMessage: ""
  });

  const emailTemplates = {
    invitation: {
      name: "Program Invitation",
      subject: "Join SKI AXIS - Transform Your Startup's Governance",
      preview: `Dear {first_name},

We're excited to invite you to join SKI AXIS, the premier accelerator for governance-focused startups in the MENA region.

As a leader at {company}, you understand the importance of strong governance frameworks. Our Digital Governance Ecology™ program has already helped 45+ startups across Climate Tech, FinTech, and Cross-Border Services.

What you'll get:
• 6-week intensive masterclass program
• 1-on-1 mentorship from industry experts
• Access to our investor network
• Governance framework templates

Special Launch Offer: $9 for the first 100 founders (normally $99)

Apply now at: [APPLY_LINK]

Best regards,
SKI AXIS Team`
    },
    followup: {
      name: "Follow-up",
      subject: "Quick follow-up: SKI AXIS Application",
      preview: `Hi {first_name},

Just wanted to follow up on our previous invitation to join SKI AXIS.

Our first cohort is filling up fast - we've already accepted 45 startups and have limited spots remaining.

Would love to have {company} be part of this transformative journey.

Questions? Reply to this email or book a call with our team.

Best,
SKI AXIS Team`
    },
    newsletter: {
      name: "Newsletter",
      subject: "SKI AXIS Weekly: Governance Insights",
      preview: `Hello {first_name},

Here's your weekly governance insights from SKI AXIS:

📊 Portfolio Update:
• 45 startups, 8/10 avg governance score
• 20+ jobs created across our ecosystem
• 87% compliance rate

🎓 Featured Mentor: Dr. Sarah Thompson
Energy Policy Expert sharing insights on regulatory compliance

📅 Upcoming Events:
• Masterclass Week 3: Financial Controls
• Investor Demo Day: March 15

Stay tuned for more updates!

SKI AXIS Team`
    }
  };

  useEffect(() => {
    fetchRecipients();
  }, []);

  useEffect(() => {
    filterRecipients();
  }, [industryFilter]);

  const fetchRecipients = async () => {
    try {
      const response = await axios.get(`${API}/recipients`);
      setRecipients(response.data);
      setSelectedRecipients(response.data.map(r => r.email));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching recipients:", error);
      setLoading(false);
    }
  };

  const filterRecipients = () => {
    if (industryFilter === "all") {
      setSelectedRecipients(recipients.map(r => r.email));
    } else {
      setSelectedRecipients(
        recipients.filter(r => r.industry === industryFilter).map(r => r.email)
      );
    }
  };

  const toggleRecipient = (email) => {
    if (selectedRecipients.includes(email)) {
      setSelectedRecipients(selectedRecipients.filter(e => e !== email));
    } else {
      setSelectedRecipients([...selectedRecipients, email]);
    }
  };

  const selectAll = () => {
    const filteredEmails = industryFilter === "all" 
      ? recipients.map(r => r.email)
      : recipients.filter(r => r.industry === industryFilter).map(r => r.email);
    setSelectedRecipients(filteredEmails);
  };

  const deselectAll = () => {
    setSelectedRecipients([]);
  };

  const sendCampaign = async () => {
    if (selectedRecipients.length === 0) {
      toast({
        title: "No recipients selected",
        description: "Please select at least one recipient",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/email/campaign`, {
        recipients: selectedRecipients,
        subject: emailData.subject,
        template: emailData.template,
        custom_message: emailData.customMessage
      });
      
      toast({
        title: "Campaign Sent!",
        description: `Successfully queued ${selectedRecipients.length} emails`,
      });
    } catch (error) {
      toast({
        title: "Error sending campaign",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    }
    setSending(false);
  };

  const getIndustryStats = () => {
    const stats = {};
    recipients.forEach(r => {
      stats[r.industry] = (stats[r.industry] || 0) + 1;
    });
    return stats;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Loading Email Campaign...</p>
        </div>
      </div>
    );
  }

  const industryStats = getIndustryStats();
  const currentTemplate = emailTemplates[emailData.template];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-700 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/")}
                className="text-gray-300 hover:text-white hover:bg-gray-800"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Home
              </Button>
              <div className="h-8 w-px bg-gray-700"></div>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-orange-600 to-amber-600 p-2 rounded-xl">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Email Campaigns</h1>
                  <p className="text-sm text-gray-400">Send bulk emails to founders</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ArrowLeft className="h-5 w-5 text-green-400" />
              <span className="text-lg font-bold text-white">SKI AXIS</span>
              <ArrowRight className="h-5 w-5 text-green-400" />
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recipients */}
          <div className="lg:col-span-1">
            <Card className="bg-gray-800/50 border-gray-700" data-testid="recipients-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Recipients
                </CardTitle>
                <CardDescription className="text-gray-400">
                  {selectedRecipients.length} of {recipients.length} selected
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filter */}
                <Select value={industryFilter} onValueChange={setIndustryFilter}>
                  <SelectTrigger className="w-full bg-gray-900 border-gray-600 text-white mb-4" data-testid="industry-filter">
                    <SelectValue placeholder="Filter by industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Industries ({recipients.length})</SelectItem>
                    {Object.entries(industryStats).map(([ind, count]) => (
                      <SelectItem key={ind} value={ind}>{ind} ({count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Select All/None */}
                <div className="flex gap-2 mb-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAll}
                    className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={deselectAll}
                    className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                  >
                    Clear
                  </Button>
                </div>

                {/* Recipients List */}
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {recipients
                    .filter(r => industryFilter === "all" || r.industry === industryFilter)
                    .map((recipient, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedRecipients.includes(recipient.email) 
                          ? 'bg-green-600/20 border border-green-500/30' 
                          : 'bg-gray-900/50 hover:bg-gray-700/50'
                      }`}
                      onClick={() => toggleRecipient(recipient.email)}
                    >
                      <Checkbox 
                        checked={selectedRecipients.includes(recipient.email)}
                        className="border-gray-600"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {recipient.first_name} {recipient.last_name}
                        </p>
                        <p className="text-gray-400 text-xs truncate">{recipient.company}</p>
                      </div>
                      <Badge variant="outline" className="text-xs text-gray-400 border-gray-600">
                        {recipient.industry?.slice(0, 3)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Email Composer */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-800/50 border-gray-700 mb-6" data-testid="compose-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="h-5 w-5 text-orange-500" />
                  Compose Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Template Selection */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Email Template</label>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(emailTemplates).map(([key, template]) => (
                      <div
                        key={key}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          emailData.template === key 
                            ? 'bg-orange-600/20 border-2 border-orange-500' 
                            : 'bg-gray-900/50 border border-gray-700 hover:border-gray-600'
                        }`}
                        onClick={() => setEmailData({
                          ...emailData, 
                          template: key,
                          subject: template.subject
                        })}
                      >
                        <p className="text-white font-medium text-sm">{template.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Subject Line</label>
                  <Input 
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    className="bg-gray-900 border-gray-600 text-white"
                    data-testid="subject-input"
                  />
                </div>

                {/* Custom Message */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Additional Message (optional)</label>
                  <Textarea 
                    value={emailData.customMessage}
                    onChange={(e) => setEmailData({...emailData, customMessage: e.target.value})}
                    placeholder="Add a personalized message..."
                    className="bg-gray-900 border-gray-600 text-white min-h-24"
                    data-testid="message-input"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex-1 border-gray-600 text-gray-300 hover:text-white"
                    data-testid="preview-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </Button>
                  <Button 
                    onClick={sendCampaign}
                    disabled={sending || selectedRecipients.length === 0}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                    data-testid="send-btn"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send to {selectedRecipients.length} Recipients
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            {showPreview && (
              <Card className="bg-gray-800/50 border-gray-700" data-testid="preview-card">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-yellow-500" />
                    Email Preview
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    How your email will look (variables will be replaced)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-white rounded-lg p-6 text-gray-800">
                    <div className="border-b pb-4 mb-4">
                      <p className="text-sm text-gray-500">Subject:</p>
                      <p className="font-semibold">{emailData.subject}</p>
                    </div>
                    <div className="whitespace-pre-line text-sm">
                      {currentTemplate.preview}
                      {emailData.customMessage && (
                        <>
                          <br /><br />
                          <p className="text-gray-600 italic">{emailData.customMessage}</p>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-6 mt-12">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ArrowLeft className="h-5 w-5 text-green-400" />
            <span className="text-lg font-bold text-white">SKI AXIS</span>
            <ArrowRight className="h-5 w-5 text-green-400" />
          </div>
          <p className="text-gray-500 text-sm">Email Campaigns - SKI Asia Pacific</p>
        </div>
      </footer>
    </div>
  );
};

export default EmailCampaignPage;
